import { useEffect, useState } from "react";
import "./SearchForm.css";
import MultiValueField from "./components/MultiValueField";

const MAX_SUGGESTIONS = 8;

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function normalizeDateInputValue(value) {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();
  if (!raw) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-");
    return `${day}/${month}/${year}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const first = Number(slashMatch[1]);
    const second = Number(slashMatch[2]);
    const year = slashMatch[3];

    if (first > 12 && second <= 12) {
      return `${padDatePart(first)}/${padDatePart(second)}/${year}`;
    }

    if (second > 12 && first <= 12) {
      return `${padDatePart(second)}/${padDatePart(first)}/${year}`;
    }

    return `${padDatePart(first)}/${padDatePart(second)}/${year}`;
  }

  const dashMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    return `${padDatePart(dashMatch[2])}/${padDatePart(dashMatch[1])}/${dashMatch[3]}`;
  }

  return "";
}

function formatDateForFilter(value) {
  if (!value) {
    return "";
  }

  const normalized = normalizeDateInputValue(value);
  if (normalized) {
    return normalized;
  }

  return String(value).trim();
}

function formatDateTyping(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function normalizeMultiValue(value) {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || "").trim())
      .filter(Boolean);
  }

  if (value === undefined || value === null) {
    return [];
  }

  const normalized = String(value).trim();
  return normalized ? [normalized] : [];
}

function buildFilterValue(values) {
  return values.length ? values : "";
}

function InputGroup({ label, hint, wide = false, children }) {
  return (
    <div className={`input-group${wide ? " input-group-wide" : ""}`}>
      <label className="input-label">{label}</label>
      {children}
      {hint && <span className="input-hint">{hint}</span>}
    </div>
  );
}

function SearchForm({ onSearch, onCancel, initialFilters = {} }) {
  const initial = typeof initialFilters === "object" && initialFilters ? initialFilters : {};

  const [idProceso, setIdProceso] = useState(normalizeMultiValue(initial.id_del_proceso));
  const [idProcesoInput, setIdProcesoInput] = useState("");
  const [entidad, setEntidad] = useState(normalizeMultiValue(initial.entidad));
  const [entidadInput, setEntidadInput] = useState("");
  const [nitEntidad, setNitEntidad] = useState(normalizeMultiValue(initial.nit_entidad));
  const [nitEntidadInput, setNitEntidadInput] = useState("");
  const [departamento, setDepartamento] = useState(normalizeMultiValue(initial.departamento));
  const [departamentoInput, setDepartamentoInput] = useState("");
  const [ciudad, setCiudad] = useState(normalizeMultiValue(initial.ciudad));
  const [ciudadInput, setCiudadInput] = useState("");
  const [nombreDescripcion, setNombreDescripcion] = useState(
    normalizeMultiValue(initial.nombre_descripcion || initial.procedimiento)
  );
  const [nombreDescripcionInput, setNombreDescripcionInput] = useState("");
  const [fechaDesde, setFechaDesde] = useState(normalizeDateInputValue(initial.fecha_desde));
  const [fechaHasta, setFechaHasta] = useState(normalizeDateInputValue(initial.fecha_hasta));
  const [precioBase, setPrecioBase] = useState(initial.precio_base || "");
  const [duracion, setDuracion] = useState(initial.duracion || "");
  const [unidad, setUnidad] = useState(initial.unidad || "");
  const [adjudicado, setAdjudicado] = useState(initial.adjudicado || "");
  const [valorAdjudicacion, setValorAdjudicacion] = useState(initial.valor_adjudicacion || "");
  const [proveedorAdjudicado, setProveedorAdjudicado] = useState(normalizeMultiValue(initial.proveedor_adjudicado));
  const [proveedorAdjudicadoInput, setProveedorAdjudicadoInput] = useState("");
  const [nitProveedor, setNitProveedor] = useState(normalizeMultiValue(initial.nit_proveedor));
  const [nitProveedorInput, setNitProveedorInput] = useState("");
  const [codigoCategoria, setCodigoCategoria] = useState(normalizeMultiValue(initial.codigo_categoria));
  const [codigoCategoriaInput, setCodigoCategoriaInput] = useState("");
  const [categoriasAdicionales, setCategoriasAdicionales] = useState(normalizeMultiValue(initial.categorias_adicionales));
  const [categoriasAdicionalesInput, setCategoriasAdicionalesInput] = useState("");

  const [entidadSuggestions, setEntidadSuggestions] = useState([]);
  const [proveedorSuggestions, setProveedorSuggestions] = useState([]);
  const [showEntidadSuggestions, setShowEntidadSuggestions] = useState(false);
  const [showProveedorSuggestions, setShowProveedorSuggestions] = useState(false);
  const [showDepartamentoSuggestions, setShowDepartamentoSuggestions] = useState(false);
  const [showCiudadSuggestions, setShowCiudadSuggestions] = useState(false);

  const [filterOptions, setFilterOptions] = useState({
    departamentos: [],
    ciudades: [],
    unidades_duracion: [],
    adjudicado: ["Si", "No"],
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await fetch("/api/analytics/filter-options/");
        if (response.ok) {
          const options = await response.json();
          setFilterOptions(options);
        }
      } catch (error) {
        console.error("Error cargando opciones de filtros:", error);
      }
    };

    loadFilterOptions();
  }, []);

  useEffect(() => {
    const searchEntidades = async () => {
      if (entidadInput.trim().length < 2) {
        setEntidadSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `/api/analytics/search-entities/?q=${encodeURIComponent(entidadInput)}&type=entidad&limit=10`
        );
        if (response.ok) {
          const results = await response.json();
          setEntidadSuggestions(results);
        }
      } catch (error) {
        console.error("Error buscando entidades:", error);
      }
    };

    const timeoutId = setTimeout(searchEntidades, 300);
    return () => clearTimeout(timeoutId);
  }, [entidadInput]);

  useEffect(() => {
    const searchProveedores = async () => {
      if (proveedorAdjudicadoInput.trim().length < 2) {
        setProveedorSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `/api/analytics/search-entities/?q=${encodeURIComponent(proveedorAdjudicadoInput)}&type=proveedor&limit=10`
        );
        if (response.ok) {
          const results = await response.json();
          setProveedorSuggestions(results);
        }
      } catch (error) {
        console.error("Error buscando proveedores:", error);
      }
    };

    const timeoutId = setTimeout(searchProveedores, 300);
    return () => clearTimeout(timeoutId);
  }, [proveedorAdjudicadoInput]);

  const departamentoSuggestions = filterOptions.departamentos
    .filter((item) => {
      const normalizedItem = item.toLowerCase();
      const normalizedInput = departamentoInput.trim().toLowerCase();
      const alreadySelected = departamento.some(value => value.toLowerCase() === normalizedItem);
      return !alreadySelected && (!normalizedInput || normalizedItem.includes(normalizedInput));
    })
    .slice(0, MAX_SUGGESTIONS);

  const ciudadSuggestions = filterOptions.ciudades
    .filter((item) => {
      const normalizedItem = item.toLowerCase();
      const normalizedInput = ciudadInput.trim().toLowerCase();
      const alreadySelected = ciudad.some(value => value.toLowerCase() === normalizedItem);
      return !alreadySelected && (!normalizedInput || normalizedItem.includes(normalizedInput));
    })
    .slice(0, MAX_SUGGESTIONS);

  const handleDateChange = (setter) => (event) => {
    setter(formatDateTyping(event.target.value));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const filtros = {
      id_del_proceso: buildFilterValue(idProceso),
      entidad: buildFilterValue(entidad),
      nit_entidad: buildFilterValue(nitEntidad),
      departamento: buildFilterValue(departamento),
      ciudad: buildFilterValue(ciudad),
      nombre_descripcion: buildFilterValue(nombreDescripcion),
      fecha_desde: formatDateForFilter(fechaDesde),
      fecha_hasta: formatDateForFilter(fechaHasta),
      precio_base: precioBase,
      duracion: duracion,
      adjudicado: adjudicado,
      valor_adjudicacion: valorAdjudicacion,
      proveedor_adjudicado: buildFilterValue(proveedorAdjudicado),
      nit_proveedor: buildFilterValue(nitProveedor),
      codigo_categoria: buildFilterValue(codigoCategoria),
      categorias_adicionales: buildFilterValue(categoriasAdicionales),
    };

    if (duracion) {
      filtros.unidad = unidad;
    }

    onSearch(filtros);
  };

  const handleClear = () => {
    setIdProceso([]);
    setIdProcesoInput("");
    setEntidad([]);
    setEntidadInput("");
    setNitEntidad([]);
    setNitEntidadInput("");
    setDepartamento([]);
    setDepartamentoInput("");
    setCiudad([]);
    setCiudadInput("");
    setNombreDescripcion([]);
    setNombreDescripcionInput("");
    setFechaDesde("");
    setFechaHasta("");
    setPrecioBase("");
    setDuracion("");
    setUnidad("");
    setAdjudicado("");
    setValorAdjudicacion("");
    setProveedorAdjudicado([]);
    setProveedorAdjudicadoInput("");
    setNitProveedor([]);
    setNitProveedorInput("");
    setCodigoCategoria([]);
    setCodigoCategoriaInput("");
    setCategoriasAdicionales([]);
    setCategoriasAdicionalesInput("");
    onSearch({});
  };

  return (
    <form className="search-form" onSubmit={handleSubmit}>
      <div className="search-form-header">
        <div>
          <h3 className="search-form-title">Filtrar licitaciones</h3>
          <p className="search-form-subtitle">
            Puedes agregar varios valores en los campos clave. Usa <strong>Enter</strong> o una{" "}
            <strong>coma</strong> para convertir cada búsqueda en una etiqueta.
          </p>
        </div>
      </div>

      <div className="form-row">
        <InputGroup label="ID del Proceso" hint="Agrega uno o varios identificadores del proceso.">
          <MultiValueField
            values={idProceso}
            onChange={setIdProceso}
            inputValue={idProcesoInput}
            onInputChange={setIdProcesoInput}
            placeholder="Ej: CO1.REQ.1027306"
          />
        </InputGroup>

        <InputGroup label="Entidad" hint="Agrega una o varias entidades para afinar la búsqueda.">
          <MultiValueField
            values={entidad}
            onChange={setEntidad}
            inputValue={entidadInput}
            onInputChange={setEntidadInput}
            placeholder="Escribe para buscar entidad"
            suggestions={entidadSuggestions}
            showSuggestions={showEntidadSuggestions}
            onShowSuggestionsChange={setShowEntidadSuggestions}
          />
        </InputGroup>

        <InputGroup label="NIT Entidad" hint="Agrega uno o varios NIT de la entidad.">
          <MultiValueField
            values={nitEntidad}
            onChange={setNitEntidad}
            inputValue={nitEntidadInput}
            onInputChange={setNitEntidadInput}
            placeholder="Ej: 899999999"
          />
        </InputGroup>
      </div>

      <div className="form-row">
        <InputGroup label="Departamento" hint="Agrega uno o varios departamentos.">
          <MultiValueField
            values={departamento}
            onChange={setDepartamento}
            inputValue={departamentoInput}
            onInputChange={setDepartamentoInput}
            placeholder="Escribe o selecciona departamento"
            suggestions={departamentoSuggestions}
            showSuggestions={showDepartamentoSuggestions}
            onShowSuggestionsChange={setShowDepartamentoSuggestions}
          />
        </InputGroup>

        <InputGroup label="Ciudad" hint="Agrega una o varias ciudades.">
          <MultiValueField
            values={ciudad}
            onChange={setCiudad}
            inputValue={ciudadInput}
            onInputChange={setCiudadInput}
            placeholder="Escribe o selecciona ciudad"
            suggestions={ciudadSuggestions}
            showSuggestions={showCiudadSuggestions}
            onShowSuggestionsChange={setShowCiudadSuggestions}
          />
        </InputGroup>

        <InputGroup label="Nombre o descripción" hint="Agrega una o varias frases para buscar en procedimiento y descripción.">
          <MultiValueField
            values={nombreDescripcion}
            onChange={setNombreDescripcion}
            inputValue={nombreDescripcionInput}
            onInputChange={setNombreDescripcionInput}
            placeholder="Escribe una frase y presiona Enter"
          />
        </InputGroup>
      </div>

      <div className="form-row">
        <InputGroup label="Fecha Desde" hint="Usa el formato DD/MM/AAAA para la fecha inicial.">
          <input
            type="text"
            value={fechaDesde}
            onChange={handleDateChange(setFechaDesde)}
            className="input"
            inputMode="numeric"
            maxLength="10"
            placeholder="DD/MM/AAAA"
          />
        </InputGroup>

        <InputGroup label="Fecha Hasta" hint="Usa el formato DD/MM/AAAA para la fecha final.">
          <input
            type="text"
            value={fechaHasta}
            onChange={handleDateChange(setFechaHasta)}
            className="input"
            inputMode="numeric"
            maxLength="10"
            placeholder="DD/MM/AAAA"
          />
        </InputGroup>

        <InputGroup label="Precio Base" hint="Ingresa el valor base que quieres consultar.">
          <input type="number" value={precioBase} onChange={e => setPrecioBase(e.target.value)} className="input" min="0" />
        </InputGroup>
      </div>

      <div className="form-row">
        <InputGroup label="Valor Adjudicación" hint="Ingresa el valor de adjudicación que deseas filtrar.">
          <input
            type="number"
            value={valorAdjudicacion}
            onChange={e => setValorAdjudicacion(e.target.value)}
            className="input"
            min="0"
          />
        </InputGroup>

        <InputGroup label="Duración" hint="Combina cantidad y unidad para filtrar la duración.">
          <div className="duration-row">
            <input
              type="number"
              value={duracion}
              onChange={e => setDuracion(e.target.value)}
              className="input"
              min="0"
            />
            <select value={unidad} onChange={e => setUnidad(e.target.value)} className="input unidad-select">
              <option value="">Unidad</option>
              {filterOptions.unidades_duracion.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>
        </InputGroup>

        <InputGroup label="Adjudicado" hint="Selecciona si el proceso fue adjudicado o no.">
          <select value={adjudicado} onChange={e => setAdjudicado(e.target.value)} className="input">
            <option value="">Seleccionar estado</option>
            {filterOptions.adjudicado.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </InputGroup>
      </div>

      <div className="form-row">
        <InputGroup label="Proveedor Adjudicado" hint="Agrega uno o varios proveedores adjudicados.">
          <MultiValueField
            values={proveedorAdjudicado}
            onChange={setProveedorAdjudicado}
            inputValue={proveedorAdjudicadoInput}
            onInputChange={setProveedorAdjudicadoInput}
            placeholder="Escribe para buscar proveedor"
            suggestions={proveedorSuggestions}
            showSuggestions={showProveedorSuggestions}
            onShowSuggestionsChange={setShowProveedorSuggestions}
          />
        </InputGroup>

        <InputGroup label="NIT Proveedor Adjudicado" hint="Agrega uno o varios NIT del proveedor.">
          <MultiValueField
            values={nitProveedor}
            onChange={setNitProveedor}
            inputValue={nitProveedorInput}
            onInputChange={setNitProveedorInput}
            placeholder="Ej: 900111222"
          />
        </InputGroup>

        <InputGroup label="Código Categoría" hint="Agrega uno o varios códigos de categoría.">
          <MultiValueField
            values={codigoCategoria}
            onChange={setCodigoCategoria}
            inputValue={codigoCategoriaInput}
            onInputChange={setCodigoCategoriaInput}
            placeholder="Ej: V1.801015"
          />
        </InputGroup>
      </div>

      <div className="form-row">
        <InputGroup label="Categorías Adicionales" hint="Agrega una o varias categorías adicionales." wide>
          <MultiValueField
            values={categoriasAdicionales}
            onChange={setCategoriasAdicionales}
            inputValue={categoriasAdicionalesInput}
            onInputChange={setCategoriasAdicionalesInput}
            placeholder="Escribe una o varias categorías"
          />
        </InputGroup>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Aplicar</button>
        <button type="button" className="btn btn-secondary" onClick={handleClear}>Limpiar</button>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
        )}
      </div>
    </form>
  );
}

export default SearchForm;
